import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Info, ArrowRight, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { GradeBadge } from "@/components/GradeBadge";
import { Reveal } from "@/components/motion/Reveal";
import { useCart } from "@/context/CartContext";
import { createOrder, createOrderCheckout } from "@/lib/api";
import { metaInitiateCheckout } from "@/lib/meta";
import { getOrderTracking, isInternalSession } from "@/lib/tracking";
import { formatPrice } from "@/lib/format";

const FIELDS = [
  { name: "full_name", label: "Nome e cognome", type: "text", required: true, col: 2, autoComplete: "name" },
  { name: "email", label: "Email", type: "email", required: true, col: 1, autoComplete: "email" },
  { name: "phone", label: "Telefono", type: "tel", required: true, col: 1, autoComplete: "tel" },
  { name: "address", label: "Indirizzo", type: "text", required: true, col: 2, autoComplete: "street-address" },
  { name: "city", label: "Città", type: "text", required: true, col: 1, autoComplete: "address-level2" },
  { name: "zip_code", label: "CAP", type: "text", required: true, col: 1, autoComplete: "postal-code" },
  { name: "province", label: "Provincia", type: "text", required: false, col: 1, autoComplete: "address-level1" },
];

export default function Checkout() {
  const { items, subtotal, clearCart } = useCart();
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", address: "", city: "", zip_code: "", province: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  if (items.length === 0) {
    return (
      <div className="container-page section" data-testid="checkout-empty">
        <div className="eyebrow">Checkout</div>
        <h1 className="display-lg mt-4">Il tuo carrello è vuoto.</h1>
        <Button asChild variant="outline" className="mt-8"><Link to="/catalogo">Vai al catalogo</Link></Button>
      </div>
    );
  }

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const missing = FIELDS.filter((f) => f.required && !form[f.name].trim());
    if (missing.length) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }
    setSubmitting(true);
    try {
      // InitiateCheckout (browser + copia server con stesso event_id).
      metaInitiateCheckout({
        value: subtotal,
        currency: "EUR",
        numItems: items.reduce((n, i) => n + i.quantity, 0),
        contents: items.map((i) => ({ id: i.variant_id || i.product_id, quantity: i.quantity, item_price: i.unit_price })),
      });
      // 1) Il server crea l'ordine "in attesa di pagamento" ricalcolando il totale dal DB.
      const payload = {
        items: items.map(({ _lineId, ...rest }) => rest),
        customer: form,
        shipping_cost: 0,
        tracking: getOrderTracking(),
        sessione_interna: isInternalSession(),
      };
      const order = await createOrder(payload);
      // 2) Il server crea la Hosted Checkout SumUp e restituisce l'URL verso cui andare.
      const res = await createOrderCheckout(order.order_number);
      clearCart();
      if (res.already_paid) {
        window.location.href = `/ordine/conferma?ordine=${order.order_number}`;
        return;
      }
      if (!res.hosted_checkout_url) throw new Error("no checkout url");
      // 3) Reindirizza all'hosted checkout sicuro di SumUp (sessione valida 30 minuti).
      window.location.href = res.hosted_checkout_url;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Impossibile avviare il pagamento", { description: "Riprova tra poco." });
      setSubmitting(false);
    }
  };

  return (
    <div className="container-page pb-24 pt-10 sm:pt-14" data-testid="checkout-page">
      <Reveal className="border-b border-border pb-10">
        <div className="eyebrow">Checkout</div>
        <h1 className="display-xl mt-4 text-foreground">Dati di spedizione</h1>
        <p className="mt-4 max-w-xl text-base text-muted-foreground sm:text-lg">Acquisto come ospite: nessun account richiesto.</p>
      </Reveal>

      <form onSubmit={handleSubmit} className="mt-8 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14" noValidate>
        <div className="space-y-6 lg:col-span-7">
          <Reveal className="rounded border border-border bg-card p-6 sm:p-8">
            <div className="mono-label text-muted-foreground">Destinatario e indirizzo</div>
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
              {FIELDS.map((f) => (
                <div key={f.name} className={f.col === 2 ? "sm:col-span-2" : ""}>
                  <Label htmlFor={f.name} className="mono-label text-muted-foreground">
                    {f.label}{f.required && <span className="ml-1 text-lime">*</span>}
                  </Label>
                  <Input
                    id={f.name}
                    type={f.type}
                    autoComplete={f.autoComplete}
                    value={form[f.name]}
                    onChange={(e) => setField(f.name, e.target.value)}
                    className="mt-2"
                    data-testid={`checkout-field-${f.name}`}
                  />
                </div>
              ))}
              <div className="sm:col-span-2">
                <Label htmlFor="notes" className="mono-label text-muted-foreground">Note per l'ordine (facoltative)</Label>
                <Textarea id="notes" value={form.notes} onChange={(e) => setField("notes", e.target.value)} className="mt-2 rounded-md border-input bg-background/40 focus-visible:ring-1 focus-visible:ring-lime" rows={3} data-testid="checkout-field-notes" />
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.05} className="rounded border border-border p-6 sm:p-8" data-testid="checkout-payment-notice">
            <div className="flex items-center justify-between gap-4">
              <div className="mono-label text-muted-foreground">Pagamento</div>
              <span className="rounded-full bg-lime/10 px-2.5 py-1 text-[11px] font-medium uppercase text-lime">SumUp</span>
            </div>
            <div className="mt-5 flex items-start gap-4">
              <Info className="h-5 w-5 shrink-0 text-lime" strokeWidth={1.5} />
              <p className="text-sm leading-relaxed text-muted-foreground">
                Cliccando “Paga” confermi i dati di spedizione e verrai reindirizzato alla pagina di
                pagamento sicuro gestita da SumUp. Al termine torni qui con l'esito dell'ordine.
              </p>
            </div>
          </Reveal>
        </div>

        {/* Riepilogo */}
        <aside className="lg:col-span-5">
          <Reveal delay={0.1} className="rounded border border-border bg-card p-6 lg:sticky lg:top-[100px]" data-testid="checkout-summary">
            <div className="mono-label text-muted-foreground">Il tuo ordine</div>
            <ul className="mt-5 divide-y divide-border">
              {items.map((item) => (
                <li key={item._lineId} className="flex items-start justify-between gap-4 py-3.5 text-sm" data-testid="checkout-summary-item">
                  <div>
                    <div className="font-medium text-foreground">{item.name}</div>
                    <div className="mt-1.5 flex items-center gap-2 font-mono text-[10.5px] uppercase tracking-mono text-muted-foreground">
                      <span>{item.brand}</span>
                      <GradeBadge grade={item.grade} compact />
                      <span>×{item.quantity}</span>
                    </div>
                  </div>
                  <div className="whitespace-nowrap font-mono text-foreground">{formatPrice(item.unit_price * item.quantity)}</div>
                </li>
              ))}
            </ul>
            <dl className="mt-4 space-y-3 border-t border-border pt-4 text-sm">
              <div className="flex justify-between"><dt className="text-muted-foreground">Subtotale</dt><dd className="font-mono">{formatPrice(subtotal)}</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">Spedizione</dt><dd className="font-mono text-[11px] uppercase tracking-mono text-lime">Gratuita</dd></div>
            </dl>
            <div className="mt-4 flex items-end justify-between border-t border-border pt-4">
              <span className="mono-label text-muted-foreground">Totale</span>
              <span className="font-mono text-2xl font-medium text-foreground" data-testid="checkout-total">{formatPrice(subtotal)}</span>
            </div>
            <Button type="submit" variant="lime" size="lg" disabled={submitting} className="mt-6 w-full justify-between" data-testid="checkout-place-order-button">
              {submitting ? "Reindirizzamento" : "Paga"} <ArrowRight strokeWidth={2} />
            </Button>
            <div className="mt-4 flex items-center gap-2 font-mono text-[10px] uppercase tracking-mono text-muted-foreground">
              <Lock className="h-3 w-3" strokeWidth={1.5} /> Pagamento sicuro gestito da SumUp · Garanzia 12 mesi · Reso 14 giorni
            </div>
          </Reveal>
        </aside>
      </form>
    </div>
  );
}
